<template>
    <v-dialog v-model="dialog" max-width="500" persistent>
        <template v-slot:activator="{ on: dialog }">
            <v-list-item v-if="activatorType === 'listItem'" v-on="{ ...dialog }" @click.stop.prevent>
                <v-list-item-icon>
                    <v-icon :color="listItemColor">{{ activatorIcon }}</v-icon>
                </v-list-item-icon>
                <v-list-item-title :class="`${listItemColor}--text mr-5`">{{ actionType }}</v-list-item-title>
            </v-list-item>
            <v-tooltip v-else bottom>
                <template v-slot:activator="{ on: tooltip }">
                    <v-btn
                        v-if="
                            actionType === actionTypes.REVOKE_INVITATION ||
                            actionType === actionTypes.REVOKE_REQUEST ||
                            actionType === actionTypes.REJECT_REQUEST
                        "
                        v-on="{ ...tooltip, ...dialog }"
                        icon
                        color="error">
                        <v-icon>clear</v-icon>
                    </v-btn>
                    <v-btn small color="success" v-else-if="actionType === actionTypes.APPROVE_REQUEST" v-on="{ ...tooltip, ...dialog }" icon>
                        <v-icon small>done</v-icon>
                    </v-btn>
                    <v-btn small v-else-if="actionType === actionTypes.RESEND_INVITATION" v-on="{ ...tooltip, ...dialog }" icon>
                        <v-icon small>reply</v-icon>
                    </v-btn>
                </template>
                <span>{{ actionType }}</span>
            </v-tooltip>
        </template>
        <v-card>
            <v-card-title class="flex-nowrap align-start">
                <div class="d-flex align-center secondary--text">
                    <span>{{ actionType }}</span>
                </div>

                <v-spacer></v-spacer>
                <v-btn :disabled="loading" @click="dialog = false" icon><v-icon>clear</v-icon></v-btn>
            </v-card-title>
            <v-spacer></v-spacer>

            <v-card-text class="mt-1">
                <div class="d-flex flex-column">
                    <span v-if="actionType === actionTypes.REVOKE_INVITATION" class="subtitle-1">Are you sure you want to revoke this invitation:</span>
                    <span v-else-if="actionType === actionTypes.REVOKE_REQUEST" class="subtitle-1">Are you sure you want to revoke this request:</span>
                    <span v-else-if="actionType === actionTypes.RESEND_INVITATION" class="subtitle-1">Are you sure you want to re-send this invitation:</span>
                    <v-card class="mt-3" flat outlined>
                        <v-card-text>
                            <div class="d-flex flex-column">
                                <span class="caption font-weight-bold text-uppercase">requestee</span>
                                {{ invitationInfo.requestee_email }}
                                <span class="caption font-weight-bold text-uppercase mb-2 mt-5">roles</span>
                                <div class="d-flex flex-column mb-1" v-for="(role, index) in sortedRoles" :key="index">
                                    <span v-if="role.role_type === invitationAndRequestRoleTypes.INSTANCE_ROLE" class="d-flex align-center">
                                        <v-icon v-if="role.Name === 'N/A'" class="mr-1 ml-1">mdi-help-circle-outline</v-icon>
                                        <v-icon v-else class="mr-1 ml-1">{{ instanceRole(role.Name).icon }}</v-icon>
                                        {{ role.Instance }}
                                    </span>
                                    <span v-else-if="role.role_type === invitationAndRequestRoleTypes.ORG_ROLE" class="d-flex align-center">
                                        <v-icon v-if="role.Name === 'N/A'" class="mr-1 ml-1">mdi-help-circle-outline</v-icon>
                                        <v-icon v-else class="mr-1 ml-1">{{ organizationRole(role.Name).icon }}</v-icon>
                                        {{ role.Organization }} ({{ organizationRole(role.Name).label }})
                                    </span>
                                    <span v-else-if="role.role_type === invitationAndRequestRoleTypes.SPACE_ROLE" class="d-flex align-center">
                                        <v-icon class="mr-1 ml-1">security</v-icon>
                                        {{ role.Space }}
                                    </span>
                                </div>
                            </div>
                        </v-card-text>
                    </v-card>
                </div>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                    v-if="actionType === actionTypes.REVOKE_INVITATION || actionType === actionTypes.REVOKE_REQUEST"
                    :loading="loading"
                    @click="revokeInvitationOrRequest"
                    color="error"
                    text>
                    Revoke
                </v-btn>

                <v-btn v-if="actionType === actionTypes.RESEND_INVITATION" :loading="loading" @click="resendInvitation" color="primary" text>Send again</v-btn>
                <v-btn v-if="actionType === actionTypes.APPROVE_REQUEST" :loading="loading" @click="approveRequest" color="success" text>Approve</v-btn>
                <v-btn v-if="actionType === actionTypes.REJECT_REQUEST" :loading="loading" @click="rejectRequest" color="error" text>Reject</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
<script>
import { invitationsAndRequests } from '@/mixins/invitationsAndRequests'
import { enumsData } from '@/mixins/enums'
import { orderBy } from 'lodash'
import { instanceRole, organizationRole } from '@/utils'

export default {
    name: 'InvitationRevokeDialog',
    mixins: [invitationsAndRequests, enumsData],
    props: {
        invitationInfo: Object,
        actionType: String,
        successFetchAction: Function,
        activatorType: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            dialog: false,
            loading: false
        }
    },
    computed: {
        activatorIcon() {
            if (this.actionType === this.actionTypes.APPROVE_REQUEST) {
                return 'done'
            }
            if (this.actionType === this.actionTypes.RESEND_INVITATION) {
                return 'reply'
            }
            return 'clear'
        },
        listItemColor() {
            if (this.actionType === this.actionTypes.APPROVE_REQUEST) {
                return 'success'
            }
            if (
                this.actionType === this.actionTypes.REVOKE_INVITATION ||
                this.actionType === this.actionTypes.REVOKE_REQUEST ||
                this.actionType === this.actionTypes.REJECT_REQUEST
            ) {
                return 'error'
            }
            return 'secondary'
        },
        sortedRoles() {
            if (!this.invitationInfo || !Array.isArray(this.invitationInfo.roles)) {
                return []
            }

            const roleOrder = {
                [this.invitationAndRequestRoleTypes.ORG_ROLE]: 0,
                [this.invitationAndRequestRoleTypes.SPACE_ROLE]: 1,
                [this.invitationAndRequestRoleTypes.INSTANCE_ROLE]: 2
            }

            return orderBy(this.invitationInfo.roles, role => roleOrder[role.role_type] ?? 99, 'asc')
        }
    },
    methods: {
        instanceRole,
        organizationRole,
        submitAction: function (apiURL, successMessage, failureMessage) {
            this.loading = true
            this.$axios
                .post(apiURL, { timeout: 300000 })
                .then(response => {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText: successMessage,
                        snackBarTimeout: 5000,
                        snackBarIcon: 'check_circle'
                    })
                    this.successFetchAction()
                })
                .catch(() => {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText: failureMessage,
                        snackBarTimeout: 10000,
                        snackBarIcon: 'error'
                    })
                })
                .finally(() => {
                    this.dialog = false
                    this.loading = false
                })
        },
        revokeInvitationOrRequest: function () {
            const apiURL =
                this.actionType === this.actionTypes.REVOKE_INVITATION
                    ? `/invitations/revoke/${this.invitationInfo.token}`
                    : `/requests/revoke/${this.invitationInfo.token}`
            const successMessage = this.actionType === this.actionTypes.REVOKE_INVITATION ? 'Invitation successfully revoked!' : 'Request successfully revoked!'
            const failureMessage =
                this.actionType === this.actionTypes.REVOKE_INVITATION
                    ? 'Failed to revoke the invitation, please try again later.'
                    : 'Failed to revoke the request, please try again later.'
            this.submitAction(apiURL, successMessage, failureMessage)
        },
        resendInvitation: function () {
            const successMessage = 'Invitation successfully resent!'
            const failureMessage = 'Failed to resend invitation, please try again later.'
            const apiURL = `/invitations/resend/${this.invitationInfo.token}`
            this.submitAction(apiURL, successMessage, failureMessage)
        },
        approveRequest: function () {
            const successMessage = 'Request successfully approved!'
            const failureMessage = 'Failed to approve the request, please try again later.'
            const apiURL = `/requests/accept/${this.invitationInfo.token}`
            this.submitAction(apiURL, successMessage, failureMessage)
        },
        rejectRequest: function () {
            const successMessage = 'Request successfully rejected!'
            const failureMessage = 'Failed to reject the request, please try again later.'
            const apiURL = `/requests/reject/${this.invitationInfo.token}`
            this.submitAction(apiURL, successMessage, failureMessage)
        }
    },
    watch: {
        globalDialog: function (nextVal, preVal) {
            this.dialog = nextVal
        }
    },
    beforeDestroy() {
        this.$store.dispatch('hideGlobalDialog')
    }
}
</script>
